import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Filter, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatPercent } from '@/lib/format';
import { toast } from '@/stores/toast';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { EmptyState } from '@/components/EmptyState';
import { useLabel } from '@/lib/labels';

/** KPI種別の単位判定（表示に使用） */
const kpiUnitOf = (kpiType: string): '%' | '¥' | '人' | '' => {
  if (kpiType === 'sales' || kpiType === 'avg_price') return '¥';
  if (kpiType === 'customer_count') return '人';
  if (kpiType.endsWith('_rate') || kpiType === 'gross_margin') return '%';
  return '';
};

/** 値を単位に応じて整形 */
const formatThresholdValue = (kpiType: string, value: number): string => {
  const unit = kpiUnitOf(kpiType);
  if (unit === '%') return formatPercent(value);
  if (unit === '¥') return `¥${Math.round(value).toLocaleString('ja-JP')}`;
  if (unit === '人') return `${Math.round(value).toLocaleString('ja-JP')} 人`;
  return String(value);
};

type Threshold = {
  kpi_threshold_id: string;
  store_id: string;
  kpi_type: string;
  threshold_value: number;
  direction: 'above' | 'below';
  effective_from: string;
  effective_to: string | null;
};

type Store = { store_id: string; store_name: string };

const kpiLabels: Record<string, string> = {
  cost_rate: '原価率',
  gross_margin: '粗利率',
  labor_rate: '人件費率',
  opex_rate: '諸経費率',
  sales: '売上高',
  customer_count: '来客数',
  avg_price: '平均単価',
};

export function KpiThresholdsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery<Threshold[]>({
    queryKey: ['kpi-thresholds'],
    queryFn: () => apiFetch<Threshold[]>('/kpi-thresholds'),
  });
  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const [editing, setEditing] = useState<Partial<Threshold> | null>(null);
  const [filterStore, setFilterStore] = useState('');
  const [filterKpi, setFilterKpi] = useState('');

  const storeName = (id: string) => stores.find((s) => s.store_id === id)?.store_name ?? id;

  const filtered = useMemo(() => rows.filter((r) => {
    if (filterStore && r.store_id !== filterStore) return false;
    if (filterKpi && r.kpi_type !== filterKpi) return false;
    return true;
  }), [rows, filterStore, filterKpi]);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort<Threshold>(filtered as unknown as Record<string, unknown>[] & Threshold[], {
    defaultKey: 'store_id',
    defaultDir: 'asc',
    accessors: {
      store_id: (r: Threshold) => storeName(r.store_id),
      kpi_type: (r: Threshold) => kpiLabels[r.kpi_type] ?? r.kpi_type,
      threshold_value: (r: Threshold) => r.threshold_value,
      direction: (r: Threshold) => r.direction,
      effective_from: (r: Threshold) => r.effective_from,
    } as unknown as Record<string, (r: Threshold) => unknown>,
  });

  const hasAnyFilter = Boolean(filterStore || filterKpi);
  const clearFilters = () => { setFilterStore(''); setFilterKpi(''); };

  const createMut = useMutation({
    mutationFn: (b: Partial<Threshold>) => apiFetch('/kpi-thresholds', { method: 'POST', body: JSON.stringify(b) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpi-thresholds'] });
      setEditing(null);
      toast.success('KPI閾値を追加しました');
    },
  });
  const updateMut = useMutation({
    mutationFn: (b: Partial<Threshold>) => apiFetch(`/kpi-thresholds/${b.kpi_threshold_id}`, {
      method: 'PATCH', body: JSON.stringify(b),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpi-thresholds'] });
      setEditing(null);
      toast.success('KPI閾値を更新しました');
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1">{t('entity.kpi_threshold_master', 'KPI閾値マスタ')}</h1>
        <button
          onClick={() => setEditing({
            kpi_type: 'cost_rate', direction: 'above',
            effective_from: new Date().toISOString().slice(0, 10),
          })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover"
        >
          <Plus size={16} /> 新規登録
        </button>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-neutral-sub" />
        <select value={filterStore} onChange={(e) => setFilterStore(e.target.value)}
          className="px-2 py-1 border border-neutral-line rounded text-sm">
          <option value="">全店舗</option>
          {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
        </select>
        <select value={filterKpi} onChange={(e) => setFilterKpi(e.target.value)}
          className="px-2 py-1 border border-neutral-line rounded text-sm">
          <option value="">全KPI種別</option>
          {Object.entries(kpiLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="flex-1" />
        <div className="text-xs text-neutral-sub tabular-nums whitespace-nowrap">
          {sortedRows.length} 件表示中（全 {rows.length} 件）
        </div>
        {hasAnyFilter && (
          <button type="button" onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-neutral-sub hover:text-brand-red">
            <X size={12} /> 絞込みをクリア
          </button>
        )}
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <SortableTh sortKey="store_id" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>{t('field.kpi_threshold_master.store_id', '店舗')}</SortableTh>
                <SortableTh sortKey="kpi_type" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>{t('field.kpi_threshold_master.kpi_type', 'KPI種別')}</SortableTh>
                <SortableTh sortKey="threshold_value" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">{t('field.kpi_threshold_master.threshold_value', '閾値')}</SortableTh>
                <SortableTh sortKey="direction" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>{t('field.kpi_threshold_master.direction', '方向')}</SortableTh>
                <SortableTh sortKey="effective_from" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>有効期間</SortableTh>
                <th scope="col" className="w-16 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r.kpi_threshold_id} className="border-t border-neutral-line">
                  <td className="px-4 py-3">{storeName(r.store_id)}</td>
                  <td className="px-4 py-3">{kpiLabels[r.kpi_type] ?? r.kpi_type}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatThresholdValue(r.kpi_type, r.threshold_value)}
                  </td>
                  <td className="px-4 py-3">{r.direction === 'above' ? '超過警告' : '未達警告'}</td>
                  <td className="px-4 py-3 text-neutral-sub tabular-nums">{r.effective_from}〜{r.effective_to ?? '（無期限）'}</td>
                  <td className="px-4 py-3">
                    <button type="button" aria-label="編集" onClick={() => setEditing(r)} className="text-brand-red">
                      <Pencil size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedRows.length === 0 && (
            <EmptyState
              message={hasAnyFilter ? 'フィルタ条件に一致する閾値がありません' : 'KPI閾値が登録されていません'}
              description={hasAnyFilter ? '絞込条件を変更するか、クリアして再表示してください' : '「＋ 新規登録」から閾値を追加してください'}
              actionLabel={hasAnyFilter ? '絞込みをクリア' : undefined}
              onAction={hasAnyFilter ? clearFilters : undefined}
            />
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditing(null)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); editing.kpi_threshold_id ? updateMut.mutate(editing) : createMut.mutate(editing); }}
            className="bg-white rounded-lg p-6 w-[500px] space-y-3"
          >
            <h2 className="text-h3 mb-2">{editing.kpi_threshold_id ? '閾値編集' : '閾値新規登録'}</h2>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.kpi_threshold_master.store_id', '店舗')}*</label>
              <select required value={editing.store_id ?? ''}
                onChange={(e) => setEditing({ ...editing, store_id: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                <option value="">— 選択 —</option>
                {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.kpi_threshold_master.kpi_type', 'KPI種別')}*</label>
              <select required value={editing.kpi_type ?? 'cost_rate'}
                onChange={(e) => setEditing({ ...editing, kpi_type: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                {Object.entries(kpiLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.kpi_threshold_master.threshold_value', '閾値')}*</label>
                <div className="relative">
                  <input required type="number" step="0.01" value={editing.threshold_value ?? ''}
                    onChange={(e) => setEditing({ ...editing, threshold_value: Number(e.target.value) })}
                    className="w-full px-3 py-2 pr-10 rounded border border-neutral-line focus-ring" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-sub text-sm pointer-events-none">
                    {kpiUnitOf(editing.kpi_type ?? 'cost_rate') || '—'}
                  </span>
                </div>
                <p className="text-xs text-neutral-sub mt-1">
                  {kpiUnitOf(editing.kpi_type ?? 'cost_rate') === '%'
                    ? '率の場合：0.37 = 37% として入力'
                    : kpiUnitOf(editing.kpi_type ?? 'cost_rate') === '¥'
                    ? '金額の場合：整数で入力（円）'
                    : '数値を直接入力'}
                </p>
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.kpi_threshold_master.direction', '方向')}</label>
                <select value={editing.direction ?? 'above'}
                  onChange={(e) => setEditing({ ...editing, direction: e.target.value as 'above' | 'below' })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                  <option value="above">超過警告</option>
                  <option value="below">未達警告</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.kpi_threshold_master.effective_from', '有効開始日')}*</label>
              <input required type="date" value={editing.effective_from ?? ''}
                onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="submit"
                className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover">保存</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
